import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';


import CategorySelect from 'common/components/CategorySelect';
import { Caption, ControlContainerRow, ControlContainer, PageSection, Subtitle } from 'common/styles/page';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  getSelectedAssessmentPeriodCategories,
  getPreviousAssessmentPeriod,
  getSelectedAssessmentPeriod,
} from '../../../common/selectors/auth';

import BubbleChart from './DashboardBubbleChart';
import LineChart from './DashboardLineChart';
import BarChart from './DashboardBarChart';
import ToggleChartControl from './DashboardChartToggle';
import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';

const DashboardMainCharts = ({ organization_id }) => {
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
  const { data: assessment_period_data, isSuccess: isDataSuccess } =
    useApiPortalV1AssessmentsResultsListQuery({
      aggregateType: 10,
      organizationId: organization_id,
    });

  const [mainChartData, setMainChartData] = useState([]);

  const previousAssessmentPeriod = useSelector(getPreviousAssessmentPeriod);
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const selectedAssessmentPeriodCategories = useSelector(getSelectedAssessmentPeriodCategories);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [defaultCategoryId, setDefaultCategoryId] = useState(null);
  const [chartType, setChartType] = useState('bubble');
  const [numAssessmentPeriods, setNumAssessmentPeriods] = useState(0);

  const subtitleMain =
    chartType === 'bar' || (chartType === 'bubble' && !previousAssessmentPeriod)
      ? 'Culture by Group'
      : chartType === 'bubble' && previousAssessmentPeriod
        ? 'Culture by Group'
        : 'Culture Trends';

  let subtitleCaption = null;
  if (chartType === 'bar' || (chartType === 'bubble' && !previousAssessmentPeriod)) {
    subtitleCaption = selectedAssessmentPeriod && selectedAssessmentPeriod.name
      ? selectedAssessmentPeriod.name
      : null;
  } else if (chartType === 'bubble' && previousAssessmentPeriod) {
    const prevName = previousAssessmentPeriod && previousAssessmentPeriod.name ? previousAssessmentPeriod.name : '';
    const selName = selectedAssessmentPeriod && selectedAssessmentPeriod.name ? selectedAssessmentPeriod.name : '';
    subtitleCaption = `${prevName} to ${selName}`;
  } else if (subtitleMain === 'Culture Trends') {
    subtitleCaption = 'Scores across all assessments';
  }

  // Load category list from selected assessment period categories selector.
  useEffect(() => {
    if (selectedAssessmentPeriodCategories && selectedAssessmentPeriodCategories.length > 0) {
      setCategories(selectedAssessmentPeriodCategories);

      const primaryCategory = selectedAssessmentPeriodCategories.find(
        category => category.priority === 'primary'
      );
      setDefaultCategoryId(primaryCategory?.id ?? null);

      if (assessment_period_data && assessment_period_data.length === 1) {
        setChartType('bar');
      }
      return;
    }

    setCategories([]);
    setDefaultCategoryId(null);
    setSelectedCategory(null);
    setMainChartData([]);
  }, [selectedAssessmentPeriodCategories, assessment_period_data, organization_id]);

  const selectedCategoryName = selectedCategory
    ? categories.find(category => category.id === selectedCategory)?.name
    : null;

  // Load all assessment data for charts
  useEffect(() => {
    if (assessment_period_data && assessment_period_data.length > 0 && selectedCategoryName) {
      const accumulatedData = [];
      let dataSetCounter = 0;

      const sortedAssessmentPeriodData = [...assessment_period_data].sort((a, b) =>
        a.result.assessment_period_start.localeCompare(b.result.assessment_period_start)
      );

      sortedAssessmentPeriodData.forEach(item => {
        const categoryData = item?.result?.aggregate_scores || [];

        const sortedCategoryData = [...categoryData]
          .filter(entry => entry.category_name === selectedCategoryName)
          .sort((a, b) => a.option_name.localeCompare(b.option_name));

        accumulatedData.push({
          id: item?.result?.assessment_period_id,
          data_set: dataSetCounter++,
          data: sortedCategoryData,
          name: item?.result?.assessment_period_name,
          start_date: item?.result?.assessment_period_start,
          end_date: item?.result?.assessment_period_end,
        });
      });
      setMainChartData(accumulatedData);
      setNumAssessmentPeriods(assessment_period_data.length);
    } else {
      setMainChartData([]);
    }
  }, [assessment_period_data, selectedCategoryName]);

  return (
    <PageSection
      padtop="15px"
      padleft="15px"
      padright="15px"
      padbottom="15px"
      style={{ height: '580px' }}
    >
      <ControlContainerRow padbottom="10px" padright="10px" padleft="0px">
        <TitleRow>
          <CardTitle>{subtitleMain}</CardTitle>
          {subtitleCaption ? <CaptionRight>{subtitleCaption}</CaptionRight> : null}
        </TitleRow>
        <ControlRow>
          <ControlContainer justify="left">
            <CategorySelect
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              defaultCategoryId={defaultCategoryId}
              width="240px" 
              placeholder="Select category"
            />
          </ControlContainer>
          <ControlContainer justify="right">
            <ToggleChartControl
              setChartType={setChartType}
              chartType={chartType}
              numAssessmentPeriods={numAssessmentPeriods}
            />
          </ControlContainer>
        </ControlRow>
      </ControlContainerRow>

      {chartType === 'bar' ? (
        <BarChart barChartData={mainChartData} />
      ) : chartType === 'bubble' && mainChartData.length > 0 ? (
        <BubbleChart bubbleChartData={mainChartData} />
      ) : (
        <LineChart lineChartData={mainChartData} />
      )}
    </PageSection>
  );
};
export default DashboardMainCharts;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

const ControlRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
`;

const CaptionRight = styled(Caption)`
  margin-left: auto;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
`;

const CardTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;
